import React, { FC, useState } from 'react';

import { Typography } from '@mui/material';
import Payment from '@solidgate/react-sdk';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';

import Loader from '~/components/atoms/Loader';
import PaymentError from '~/components/molecules/PaymentError';
import { usePaymentFormLogic } from '~/components/organisms/SolidCheckout/hooks/usePaymentFormLogic';
import { Typographies } from '~/theme/typography';
import { PAYMENT_METHODS, SOLID_PAYMENT_METHOD_MAPPER } from '~/types/payment';

import { sliderFormParams, sliderFormStyles } from './formConfig';
import PaymentButton from './PaymentButton';
import styles from './styles.module.scss';
import { PaymentFormProps } from '../../../types';

const PaymentFormV1: FC<PaymentFormProps> = ({
  onPaymentSuccess,
  onPaymentFail,
  onPaymentSubmit,
  onPaymentMethodChange,
  onCreateCardIntentError,
  onFormReady,
  paymentApi,
}) => {
  const { t } = useTranslation('general');
  const [activeCard, setActiveCard] = useState<PAYMENT_METHODS>(
    PAYMENT_METHODS.ONE_PAY,
  );

  const {
    isLoading,
    paymentError,
    merchantData,
    paypalBtnRef,
    appleContainerRef,
    googleContainerRef,
    handleCloseError,
    handlePaymentSuccess,
    handlePaymentFail,
    handlePaymentSubmit,
    isPaypalAvailable,
  } = usePaymentFormLogic({
    paymentApi,
    onPaymentSuccess,
    onPaymentFail,
    onPaymentSubmit,
    onCreateCardIntentError,
    onFormReady,
  });

  const handlePaymentButtonClick = (type: PAYMENT_METHODS): void => {
    onPaymentMethodChange(type);
    setActiveCard(type);
  };

  return (
    <div className={styles.container}>
      {isLoading && <Loader />}

      {paymentError && (
        <PaymentError
          paymentError={paymentError}
          handleCloseError={handleCloseError}
        />
      )}

      {merchantData && !paymentError && (
        <Swiper
          slidesPerView={1}
          spaceBetween={16}
          initialSlide={0}
          centeredSlides
          onSwiper={(swiper): void => {
            activeCard === PAYMENT_METHODS.ONE_PAY && swiper.slideNext(); // By invoking this method we trigger slider for IOS Chrome
          }}
          className={clsx(styles.slider, {
            [styles.is_disabled]: isLoading,
          })}
        >
          <div className={styles.buttons}>
            {[PAYMENT_METHODS.ONE_PAY, PAYMENT_METHODS.CARD].map((type) => (
              <PaymentButton
                key={type}
                isActive={activeCard === type}
                onClick={handlePaymentButtonClick}
                type={type}
              />
            ))}
          </div>

          <SwiperSlide className={styles.slide}>
            <Typography
              className={styles.card_title}
              variant={Typographies.LABEL_LARGE}
              component="p"
            >
              {t('subscription:payment_form.card_details', 'Card details')}
            </Typography>

            <Payment
              googlePayContainerRef={googleContainerRef}
              applePayContainerRef={appleContainerRef}
              merchantData={merchantData}
              formParams={sliderFormParams(t)}
              styles={sliderFormStyles}
              onFail={(e): void =>
                handlePaymentFail(
                  e.message || '',
                  SOLID_PAYMENT_METHOD_MAPPER[e.entity],
                )
              }
              onSuccess={async (e): Promise<void> =>
                await handlePaymentSuccess(e.order)
              }
              onMounted={(): void => {
                window.setTimeout(() => {
                  onFormReady && onFormReady();
                }, 500);
              }}
              onSubmit={(e): void =>
                handlePaymentSubmit(SOLID_PAYMENT_METHOD_MAPPER[e.entity])
              }
            />
          </SwiperSlide>

          <SwiperSlide className={styles.slide}>
            <div
              className={styles['slide__pay_button']}
              ref={appleContainerRef}
            />
            <div
              className={styles['slide__pay_button']}
              ref={googleContainerRef}
            />
            {isPaypalAvailable && (
              <div
                className={styles['slide__pay_button']}
                id="paypal-button"
                ref={paypalBtnRef}
              />
            )}
          </SwiperSlide>
        </Swiper>
      )}
    </div>
  );
};

export default PaymentFormV1;
